import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import { ArrowLeft, Share2, FileText, Award, GraduationCap, User, Hash, Shield } from "lucide-react";
import { format } from "date-fns";
import { notify } from "@/lib/utils/toast";
import { CSSProperties, memo } from "react";

interface DetailProps {
	data: any;
	onBack?: () => void;
}

export default function DetailDegreeOrCertificate({ data, onBack }: DetailProps) {

	if (!data) {
		return <div className="flex items-center justify-center h-screen">
			<p className="text-lg">Đang tải.</p>
		</div>
	}

	const getCertificateIcon = (type: string) => {
		switch (type) {
			case "bachelor":
				return <GraduationCap className='w-6 h-6' />;
			case "master":
				return <GraduationCap className='w-6 h-6' />;
			case "language":
				return <FileText className='w-6 h-6' />;
			case "gdqp":
				return <Shield className='w-6 h-6' />;
			case "award":
				return <Award className='w-6 h-6' />;
			default:
				return <FileText className='w-6 h-6' />;
		}
	};

	const getTypeLabel = (type: string) => {
		switch (type) {
			case "bachelor":
				return "bằng Đại học";
			case "master":
				return "bằng Thạc sĩ";
			case "language":
				return "chứng chỉ";
			case "gdqp":
				return "chứng chỉ giáo dục quốc phòng";
			case "award":
				return "khen thưởng";
			case "scholarship":
				return "Khen thưởng - Chứng nhận";
			default:
				return "khác";
		}
	};

	const vstepConvertLevel = (level: string) => {
		switch (level) {
			case "B1":
				return "Bậc 3";
			case "B2":
				return "Bậc 4";
			case "C1":
				return "Bậc 5";
			default:
				return level;
		}
	}

	return (
		<>
			<div className='flex items-center gap-4'>
				{onBack !== undefined && (
					<Button variant='outline' onClick={onBack}>
						<ArrowLeft className='w-4 h-4' />
						Tra cứu
					</Button>
				)}
				<div className='flex gap-2'>
					{/* <Button variant='outline' size='sm'>
						<Download className='w-4 h-4' />
						Tải xuống
					</Button> */}
					<Button
						onClick={() => {
							navigator.clipboard
								.writeText(`${location.host}?type=${data.type}&id=${data.id}`)
								.then(() => notify.success("Đã copy link chia sẻ"))
								.catch((err) => notify.error("Copy link chia sẻ không thành công"));
						}}
						variant='outline'
						size='sm'
					>
						<Share2 className='w-4 h-4' />
						Chia sẻ
					</Button>
				</div>
			</div>

			<Card className='border border-[#1F2251]/10 overflow-hidden'>
				<CardHeader className='bg-[#1F2251] text-white pb-3'>
					<div className='flex items-start justify-between'>
						<div className='flex items-center gap-3'>
							<div className='w-12 h-12 bg-white/10 rounded-full flex items-center justify-center'>{getCertificateIcon(data.type)}</div>
							<div>
								<CardTitle className='text-xl font-bold' style={{ textTransform: "capitalize" }}>
									{data.title}
								</CardTitle>
								<p className=''>{data.institution}</p>
							</div>
						</div>
						{/* <div className='flex flex-col gap-1 items-end'>
										<Badge variant='outline' className='text-sm'>
											{getTypeLabel(data.type)}
										</Badge>
									</div> */}
					</div>
				</CardHeader>
				<CardContent className='p-8 space-y-6'>
					<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
						<div className='space-y-4'>
							<h3 className='text-lg flex items-center gap-2'>
								<User className='w-5 h-5' />
								Thông tin cá nhân
							</h3>
							<div className='space-y-3 pl-7'>
								<InfoSection title={data.type == "language" ? "Số báo danh" : "Mã sinh viên"} content={data.studentId} />
								<InfoSection title='Họ và tên' content={data.studentName} style={{ textTransform: "capitalize" }} />
								<InfoSection title='Giới tính' content={data.gender} />
								<InfoSection title='Ngày sinh' content={data.dateOfBirth ? format(data.dateOfBirth, "dd/MM/yyyy") : ""} />
								<div style={{ textTransform: 'capitalize' }}>
									<InfoSection title='Ngành' content={data.originalData.major} />
								</div>

								{/* <InfoSection title='Số CCCD' content={data.cccd} /> */}
							</div>
						</div>

						<div className='space-y-4'>
							<h3 className='text-lg flex items-center gap-2'>
								<Hash className='w-5 h-5' />
								Thông tin {getTypeLabel(data.type)}
							</h3>
							<div className='space-y-3 pl-7'>
								<InfoSection title={data.type == 'gdqp' ? 'Xếp loại' : 'Xếp loại/xếp hạng tốt nghiệp'} content={data.originalData.classification} />
								<InfoSection title='Số hiệu' content={data.originalData.degreeNumber} />
								<InfoSection title='Số hiệu' content={data.originalData.certificateNumber} />
								<InfoSection title='Số vào sổ gốc cấp văn bằng' content={data.originalData.registrationNumber} />
								<InfoSection title='Quyết định' content={data.originalData.recognitionDecisionNumber} />
								<InfoSection title='Ngôn ngữ' content={data.originalData.language} />
								<InfoSection title='Trình độ' content={data.originalData.level} />
								{/* <InfoSection title='Mã KT-CN' content={data.originalData.scholarshipCode} /> */}
								<InfoSection title='Số Quyết định' content={data.originalData.certificateNo} />
								<InfoSection title='Năm học' content={data.originalData.year} />
								<InfoSection title='Học kỳ' content={data.originalData.semester} />
								<InfoSection title='Danh hiệu' content={data.originalData.title} />
								<InfoSection title='Ngày ký' content={data.signDate ? format(data.signDate, "dd/MM/yyyy") : ""} />
							</div>
						</div>
					</div>

					{data.description && (
						<>
							<Separator />
							<div>
								<h3 className='text-lg mb-3'>Ghi chú</h3>
								<p className='text-muted-foreground bg-muted/50 p-4 rounded-lg'>{data.description}</p>
							</div>
						</>
					)}

					{/* <Separator /> */}

					{/* <div className='bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4'>
						<div className='flex items-center gap-2 text-green-700 dark:text-green-400 mb-2'>
							<Shield className='w-5 h-5' />
							<span>Xác thực thành công</span>
						</div>
						<p className='text-sm text-green-600 dark:text-green-300'>
							<span>{getTypeLabel(data.type)}</span>
							này đã được xác thực và có hiệu lực. Thông tin trên đây được cấp bởi {data.institution} và được xác nhận tính chính xác vào ngày {new Date().toLocaleDateString("vi-VN")}.
						</p>
					</div> */}
				</CardContent>
			</Card>
		</>
	);
}

const InfoSection = memo(({ title, content, style }: { title: string; content?: string; style?: CSSProperties }) => {
	if (!content) {
		return <></>;
	}
	return (
		<>
			<div className='hidden justify-between md:flex'>
				<span className='text-muted-foreground'>{title}:</span>
				<span style={style}>{content}</span>
			</div>
			<div className='block md:hidden'>
				<span className='text-muted-foreground'>{title}:</span>
				<br />
				<span style={style}>{content}</span>
			</div>
		</>
	);
});
